import { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EmailSignupForm from "@/components/EmailSignupForm";
import WaitlistCount from "@/components/WaitlistCount";
import { isWynnHost } from "@/lib/domain";
import {
  Sparkles,
  CalendarHeart,
  Headphones,
  BookOpen,
  HeartPulse,
  NotebookPen,
  Users,
  CheckCircle2,
  ArrowUpRight,
} from "lucide-react";

const WYNN_LOGO = "https://customer-assets.emergentagent.com/job_five-min-formula/artifacts/xl70jip2_WYNN%20%282%29.png";
const WYNN_MARK = "https://customer-assets.emergentagent.com/job_five-min-formula/artifacts/xl70jip2_WYNN%20%282%29.png";

// App screenshots
const SHOT_SPLASH = "https://customer-assets.emergentagent.com/job_five-min-formula/artifacts/j2lk43hm_IMG_9615.PNG";
const SHOT_HOME_AFFIRMATION = "https://customer-assets.emergentagent.com/job_five-min-formula/artifacts/29elgarj_IMG_9617.PNG";
const SHOT_HOME_EXPLORE = "https://customer-assets.emergentagent.com/job_five-min-formula/artifacts/cijkhpkm_IMG_9616.PNG";

const features = [
  {
    icon: Sparkles,
    label: "AI Self-Coaching",
    body: "Personalised guidance for the moments you feel stuck. A gentle, intelligent voice meeting you where you are.",
  },
  {
    icon: CalendarHeart,
    label: "Daily affirmations",
    body: "A short, hand-written affirmation that opens each day. Something to carry into the studio, or into the rest of life.",
  },
  {
    icon: BookOpen,
    label: "Seven creative courses",
    body: "Programs to walk you back to your work. The 5-Minute Reset, The Becoming, sound bath libraries and more.",
  },
  {
    icon: Headphones,
    label: "Meditations & sound",
    body: "Two libraries of meditations and sound healing for resets, openings, evenings, and the days you can't.",
  },
  {
    icon: NotebookPen,
    label: "Mood journaling",
    body: "A quiet daily check-in. Track what's moving through you and notice the patterns underneath your work.",
  },
  {
    icon: HeartPulse,
    label: "Pain relief practices",
    body: "Short video practices for the body that holds your work. Gentle, embodied, made to be used.",
  },
  {
    icon: Users,
    label: "A small community",
    body: "Connect with other artists on their own wellness path. Quiet, generous, intentionally small.",
  },
  {
    icon: CalendarHeart,
    label: "Coaching sessions",
    body: "Book a free creative alignment consultation straight from the app. Doors into the deeper work.",
  },
];

const betaPerks = [
  "Lifetime access at founding-member pricing",
  "First to shape the prompts, voice and rituals inside",
  "A private channel to whisper feedback directly to me",
  "Your name in the credits of v1, if you want it there",
];

// Small phone mockup component
function PhoneFrame({ src, alt, label, accent }) {
  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <div
          aria-hidden
          className="absolute -inset-6 blur-3xl opacity-50 -z-10 rounded-[60px]"
          style={{ background: accent }}
        />
        <div className="relative w-[260px] md:w-[280px] aspect-[9/19.5] rounded-[44px] p-[10px] bg-[#0A0910] border border-[#F4F1EC]/15 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]">
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 rounded-full bg-[#0A0910] border border-[#F4F1EC]/10 z-10" />
          <div className="relative w-full h-full rounded-[36px] overflow-hidden bg-white">
            <img
              src={src}
              alt={alt}
              className="w-full h-full object-cover object-top block"
              draggable="false"
            />
          </div>
        </div>
      </div>
      <div className="mt-6 text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4F1EC]/55 font-body">
          {label}
        </div>
      </div>
    </div>
  );
}

export default function Wynn() {
  const wynnOnly = isWynnHost();

  useEffect(() => {
    document.title = "WYNN · Creative transformation in your pocket";
    const desc = "WYNN. A pocket companion for the inner artist. AI self-coaching, daily affirmations, meditations, journaling, and sound healing. Join the closed beta.";
    const setMeta = (sel, attr, val) => {
      let el = document.head.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', desc);
    setMeta('meta[property="og:title"]', 'content', 'WYNN · Creative transformation in your pocket');
    setMeta('meta[property="og:description"]', 'content', desc);
    setMeta('meta[name="twitter:description"]', 'content', desc);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div data-testid="wynn-page" className="min-h-screen bg-[#070608] text-[#F4F1EC] flex flex-col">
      {!wynnOnly && <Navbar />}

      <main className="flex-1">
        {/* Hero */}
        <section data-testid="wynn-hero" className="relative overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.55]"
            style={{
              background:
                "radial-gradient(60% 50% at 78% 22%, rgba(255,150,200,0.35) 0%, rgba(7,6,8,0) 60%), radial-gradient(45% 40% at 15% 78%, rgba(120,200,230,0.30) 0%, rgba(7,6,8,0) 65%), radial-gradient(50% 45% at 50% 95%, rgba(180,140,230,0.22) 0%, rgba(7,6,8,0) 70%)",
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)",
              backgroundSize: "3px 3px",
              opacity: 0.35,
              mixBlendMode: "overlay",
            }}
          />

          <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24 md:pb-32">
            <div className="grid grid-cols-12 gap-10 items-center">
              <div className="col-span-12 md:col-span-7">
                <div className="flex items-center gap-3 mb-6">
                  <span className="inline-block h-1 w-8 bg-gradient-to-r from-[#FFB7C6] via-[#C7A8FF] to-[#A8E6FF]" />
                  <span className="text-[11px] uppercase tracking-[0.32em] text-[#F4F1EC]/60 font-body">
                    WYNN · Closed beta · Coming soon
                  </span>
                </div>

                <h1
                  data-testid="wynn-headline"
                  className="font-display tracking-tighter leading-[0.92] text-[clamp(3rem,8.6vw,7rem)] font-medium text-[#F4F1EC]"
                >
                  Creative <br />
                  transformation, <br />
                  <span className="italic font-light bg-gradient-to-r from-[#FFB7C6] via-[#E6BFFF] to-[#A8E6FF] bg-clip-text text-transparent">
                    in your pocket
                  </span>
                  .
                </h1>

                <p className="mt-8 font-body text-[#F4F1EC]/75 text-lg md:text-xl leading-[1.7] max-w-2xl">
                  WYNN is a small, intelligent companion for artists and
                  makers. AI self-coaching, daily affirmations, meditations,
                  sound healing, mood journaling, body practices and a quiet
                  community, all in one calm pocket-sized space. Built by hand
                  to walk with you back to your work.
                </p>

                <div className="mt-10 max-w-xl">
                  <p className="text-[11px] uppercase tracking-[0.28em] text-[#F4F1EC]/55 font-body mb-4">
                    Join the waitlist
                  </p>
                  <EmailSignupForm
                    source="wynn_app"
                    testIdPrefix="wynn-hero"
                    dark
                    redirectTo={null}
                    successDescription="You're on the WYNN waitlist. Beta invites go out first."
                  />
                  <div className="mt-5">
                    <WaitlistCount
                      sourcePrefix="wynn_app"
                      threshold={50}
                      testId="wynn-hero-waitlist-count"
                      firstCircleCopy="Be among the first to step inside. The first quiet circle."
                    />
                  </div>
                  <p className="mt-3 font-body text-xs text-[#F4F1EC]/45 leading-relaxed">
                    Beta testers keep founding-member pricing for life. Easy
                    to leave any time.
                  </p>
                </div>
              </div>

              <aside
                className="col-span-12 md:col-span-5 flex justify-center md:justify-end"
                data-testid="wynn-logo-card"
              >
                <div className="relative">
                  <div
                    aria-hidden
                    className="absolute inset-0 blur-3xl opacity-70 -z-10"
                    style={{
                      background:
                        "radial-gradient(50% 50% at 50% 50%, rgba(255,170,210,0.55) 0%, rgba(170,200,240,0.35) 45%, rgba(7,6,8,0) 75%)",
                    }}
                  />
                  <img
                    src={WYNN_LOGO}
                    alt="WYNN app icon"
                    className="relative w-[280px] md:w-[360px] h-auto select-none drop-shadow-[0_30px_60px_rgba(0,0,0,0.55)]"
                    draggable="false"
                  />
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Why WYNN exists */}
        <section data-testid="wynn-why" className="relative border-t border-[#F4F1EC]/10 bg-[#0A0910]">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="grid grid-cols-12 gap-8 items-end mb-12">
              <div className="col-span-12 md:col-span-7">
                <div className="text-[11px] uppercase tracking-[0.32em] text-[#F4F1EC]/55 font-body">
                  Why WYNN exists
                </div>
                <h2 className="mt-4 font-display tracking-tight leading-[1.02] text-[clamp(2rem,4.6vw,3.6rem)] font-medium text-[#F4F1EC]">
                  The work doesn&apos;t leave you. <br />
                  <span className="italic font-light text-[#F4F1EC]/80">
                    It just goes quiet.
                  </span>
                </h2>
              </div>
              <div className="col-span-12 md:col-span-5">
                <p className="font-body text-[#F4F1EC]/70 leading-[1.8]">
                  WYNN is the companion I wished I had on the days the studio
                  felt heavy. A small kit of practices, meditations, prompts
                  and intelligent coaching, all in one place. Built slowly, by
                  hand, for artists who already carry enough noise in a day.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* App preview phones */}
        <section data-testid="wynn-preview" className="relative border-t border-[#F4F1EC]/10 overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(7,6,8,1) 0%, rgba(15,12,20,1) 50%, rgba(7,6,8,1) 100%)",
            }}
          />
          <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
            <div className="grid grid-cols-12 gap-8 items-end mb-16">
              <div className="col-span-12 md:col-span-8">
                <div className="text-[11px] uppercase tracking-[0.32em] text-[#F4F1EC]/55 font-body">
                  A look inside
                </div>
                <h2 className="mt-4 font-display tracking-tight leading-[1.02] text-[clamp(2rem,4.6vw,3.5rem)] font-medium text-[#F4F1EC]">
                  Less screen.{" "}
                  <span className="italic font-light text-[#F4F1EC]/80">
                    More studio.
                  </span>
                </h2>
                <p className="mt-5 font-body text-[#F4F1EC]/70 text-lg leading-[1.75] max-w-2xl">
                  Three quiet doorways into the app: the gentle opening, the
                  daily affirmation, and the library of practices waiting for
                  you to choose what calls today.
                </p>
              </div>
            </div>

            <div
              data-testid="wynn-phone-gallery"
              className="grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-8 justify-items-center"
            >
              <PhoneFrame
                src={SHOT_SPLASH}
                alt="WYNN splash screen"
                label="The opening"
                accent="radial-gradient(50% 50% at 50% 50%, rgba(255,170,210,0.4) 0%, rgba(7,6,8,0) 70%)"
              />
              <PhoneFrame
                src={SHOT_HOME_AFFIRMATION}
                alt="WYNN home with daily affirmation"
                label="Daily affirmation"
                accent="radial-gradient(50% 50% at 50% 50%, rgba(230,191,255,0.45) 0%, rgba(7,6,8,0) 70%)"
              />
              <PhoneFrame
                src={SHOT_HOME_EXPLORE}
                alt="WYNN home explore grid"
                label="The library"
                accent="radial-gradient(50% 50% at 50% 50%, rgba(168,230,255,0.4) 0%, rgba(7,6,8,0) 70%)"
              />
            </div>
          </div>
        </section>

        {/* Features grid */}
        <section data-testid="wynn-features" className="relative border-t border-[#F4F1EC]/10 bg-[#0A0910]">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-28">
            <div className="grid grid-cols-12 gap-8 items-end mb-14">
              <div className="col-span-12 md:col-span-7">
                <div className="text-[11px] uppercase tracking-[0.32em] text-[#F4F1EC]/55 font-body">
                  Eight quiet doorways
                </div>
                <h2 className="mt-4 font-display tracking-tight leading-[1.02] text-[clamp(2rem,4.6vw,3.6rem)] font-medium text-[#F4F1EC]">
                  Everything you need to walk back to{" "}
                  <span className="italic font-light text-[#F4F1EC]/80">
                    the work
                  </span>
                  .
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-6 md:gap-8">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.label}
                    data-testid={`wynn-feature-${i + 1}`}
                    className="col-span-12 sm:col-span-6 lg:col-span-3 border border-[#F4F1EC]/12 bg-[#F4F1EC]/[0.025] p-7 hover:border-[#FFB7C6]/40 transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-display text-[20px] leading-none tracking-tight text-[#F4F1EC]/40">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <Icon
                        size={20}
                        strokeWidth={1.4}
                        className="text-[#FFB7C6] group-hover:text-[#A8E6FF] transition-colors"
                      />
                    </div>
                    <h3 className="mt-8 font-display text-[22px] leading-tight text-[#F4F1EC]">
                      {f.label}
                    </h3>
                    <p className="mt-3 font-body text-[14px] leading-[1.7] text-[#F4F1EC]/65">
                      {f.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Beta invitation */}
        <section data-testid="wynn-beta" className="relative border-t border-[#F4F1EC]/10">
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,170,210,0.08) 0%, rgba(120,200,230,0.08) 50%, rgba(180,140,230,0.08) 100%)",
            }}
          />
          <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
            <div className="grid grid-cols-12 gap-10 items-start">
              <div className="col-span-12 md:col-span-6">
                <div className="text-[11px] uppercase tracking-[0.32em] text-[#FFB7C6] font-body">
                  Beta testers wanted
                </div>
                <h2 className="mt-4 font-display tracking-tight leading-[1.02] text-[clamp(2rem,4.6vw,3.6rem)] font-medium text-[#F4F1EC]">
                  Help me shape <br />
                  <span className="italic font-light bg-gradient-to-r from-[#FFB7C6] via-[#E6BFFF] to-[#A8E6FF] bg-clip-text text-transparent">
                    something gentle
                  </span>
                  .
                </h2>
                <p className="mt-6 font-body text-[#F4F1EC]/75 text-lg leading-[1.75] max-w-xl">
                  I&apos;m inviting a small first circle of artists into the
                  closed beta. You&apos;ll get the app early, weave your voice
                  into the prompts and rituals, and keep founding-member
                  pricing for life. Quiet, generous, and a little sacred.
                </p>

                <ul className="mt-10 space-y-4">
                  {betaPerks.map((perk, i) => (
                    <li
                      key={i}
                      data-testid={`wynn-perk-${i + 1}`}
                      className="flex items-start gap-3 font-body text-[15px] text-[#F4F1EC]/85 leading-relaxed"
                    >
                      <CheckCircle2
                        size={18}
                        strokeWidth={1.5}
                        className="text-[#A8E6FF] mt-0.5 shrink-0"
                      />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="col-span-12 md:col-span-5 md:col-start-8">
                <div className="border border-[#F4F1EC]/15 bg-[#F4F1EC]/[0.035] backdrop-blur-sm p-8 md:p-10">
                  <div className="flex items-center gap-4 mb-6">
                    <img
                      src={WYNN_MARK}
                      alt=""
                      className="w-14 h-14 object-contain"
                      aria-hidden
                    />
                    <div>
                      <div className="font-display text-[22px] leading-none tracking-tight text-[#F4F1EC]">
                        Join the beta circle
                      </div>
                      <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-[#F4F1EC]/55 font-body">
                        Small first circle · Free to join
                      </div>
                    </div>
                  </div>

                  <EmailSignupForm
                    source="wynn_app_beta"
                    testIdPrefix="wynn-beta"
                    dark
                    redirectTo={null}
                    successDescription="You're on the beta list. I'll reach out personally when invites open."
                  />

                  <p className="mt-6 font-body text-xs text-[#F4F1EC]/45 leading-relaxed">
                    Same waitlist, deeper involvement. Mark this if you want
                    to test, give feedback, and help me build it gently.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Status / detail strip */}
        <section data-testid="wynn-status" className="bg-[#0A0910] border-t border-[#F4F1EC]/10">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-24">
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-12 md:col-span-7 space-y-6 font-body text-[#F4F1EC]/75 text-[16px] leading-[1.85]">
                <div className="text-[11px] uppercase tracking-[0.32em] text-[#F4F1EC]/55 font-body">
                  Made in a quiet room
                </div>
                <p>
                  WYNN is being built with the same hand as the Academy and
                  the podcast. Same teal thread, same trust in your
                  intuition, same refusal to turn creativity into a
                  productivity game.
                </p>
                <p>
                  It opens, it greets you, it offers a doorway. You finish,
                  you close the app, you return to the work. That&apos;s the
                  whole loop. Underneath, a library of practices grows
                  quietly alongside you.
                </p>
              </div>

              <div className="col-span-12 md:col-span-4 md:col-start-9">
                <div className="border border-[#F4F1EC]/12 bg-[#F4F1EC]/[0.03] p-7 space-y-5 font-body text-[13px]">
                  <div className="flex justify-between border-b border-[#F4F1EC]/10 pb-3">
                    <span className="text-[#F4F1EC]/55">Status</span>
                    <span className="text-[#F4F1EC]">In private build</span>
                  </div>
                  <div className="flex justify-between border-b border-[#F4F1EC]/10 pb-3">
                    <span className="text-[#F4F1EC]/55">Platforms</span>
                    <span className="text-[#F4F1EC]">iOS first</span>
                  </div>
                  <div className="flex justify-between border-b border-[#F4F1EC]/10 pb-3">
                    <span className="text-[#F4F1EC]/55">Beta opens</span>
                    <span className="text-[#F4F1EC]">2026</span>
                  </div>
                  <div className="flex justify-between border-b border-[#F4F1EC]/10 pb-3">
                    <span className="text-[#F4F1EC]/55">Made by</span>
                    <span className="text-[#F4F1EC]">Maria-Katharina</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#F4F1EC]/55">For</span>
                    <span className="text-[#F4F1EC]">Quiet creators</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section data-testid="wynn-final" className="relative bg-[#070608] border-t border-[#F4F1EC]/10 overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 opacity-60"
            style={{
              background:
                "radial-gradient(50% 60% at 50% 100%, rgba(255,170,210,0.30) 0%, rgba(7,6,8,0) 70%), radial-gradient(40% 50% at 80% 20%, rgba(168,230,255,0.22) 0%, rgba(7,6,8,0) 70%)",
            }}
          />
          <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 py-28 md:py-36">
            <div className="grid grid-cols-12 gap-8 items-end">
              <div className="col-span-12 md:col-span-8">
                <div className="text-[11px] uppercase tracking-[0.32em] text-[#F4F1EC]/55 font-body">
                  Be among the first
                </div>
                <h2 className="mt-4 font-display tracking-tight leading-[0.98] text-[clamp(2.5rem,5.6vw,4.8rem)] font-medium text-[#F4F1EC]">
                  When WYNN opens, <br />
                  <span className="italic font-light bg-gradient-to-r from-[#FFB7C6] via-[#E6BFFF] to-[#A8E6FF] bg-clip-text text-transparent">
                    you&apos;ll be inside the first circle
                  </span>
                  .
                </h2>
                <p className="mt-6 font-body text-[#F4F1EC]/70 text-lg leading-[1.75] max-w-2xl">
                  No launch noise. No countdown timers. A simple letter when
                  the doors quietly open. Until then, the rest of the work
                  lives over at the Academy and on the podcast.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  {!wynnOnly && (
                    <>
                      <a
                        href="/"
                        data-testid="wynn-go-academy"
                        className="inline-flex items-center gap-2 border border-[#F4F1EC]/40 hover:border-[#FFB7C6] hover:text-[#FFB7C6] text-[#F4F1EC] font-body text-[12px] uppercase tracking-[0.24em] h-11 px-5 transition-colors"
                      >
                        Visit the Academy
                        <ArrowUpRight size={14} strokeWidth={1.6} />
                      </a>
                      <a
                        href="/born-to-create"
                        data-testid="wynn-go-podcast"
                        className="inline-flex items-center gap-2 border border-[#F4F1EC]/40 hover:border-[#A8E6FF] hover:text-[#A8E6FF] text-[#F4F1EC] font-body text-[12px] uppercase tracking-[0.24em] h-11 px-5 transition-colors"
                      >
                        Listen to the podcast
                        <ArrowUpRight size={14} strokeWidth={1.6} />
                      </a>
                      <a
                        href="/work-with-me"
                        data-testid="wynn-go-wwm"
                        className="inline-flex items-center gap-2 border border-[#F4F1EC]/40 hover:border-[#E6BFFF] hover:text-[#E6BFFF] text-[#F4F1EC] font-body text-[12px] uppercase tracking-[0.24em] h-11 px-5 transition-colors"
                      >
                        Work with Maria
                        <ArrowUpRight size={14} strokeWidth={1.6} />
                      </a>
                    </>
                  )}
                </div>
              </div>

              <div className="col-span-12 md:col-span-4">
                <EmailSignupForm
                  source="wynn_app_final"
                  testIdPrefix="wynn-final"
                  dark
                  redirectTo={null}
                  successDescription="You're on the WYNN list. See you on the inside."
                />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
